import React, { useEffect, useState } from 'react';
import {
  Table,
  Card,
  Button,
  Space,
  Tag,
  Modal,
  Form,
  Input,
  InputNumber,
  Switch,
  message,
  Popconfirm,
  Divider,
  Row,
  Col,
  Select,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  MinusCircleOutlined,
  BankOutlined,
  CreditCardOutlined,
  WechatOutlined,
  DollarOutlined,
} from '@ant-design/icons';
import { get, post, put, del } from '../../utils/api';

const { Option } = Select;
const { TextArea } = Input;

const WithdrawMethods = () => {
  const [methods, setMethods] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingMethod, setEditingMethod] = useState(null);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchMethods();
  }, []);

  // 获取提现方式列表
  const fetchMethods = async () => {
    setLoading(true);
    try {
      const res = await get('/admin/withdraw-methods');
      if (res.code === 200) {
        const list = res.data?.list || res.data || [];
        // 确保使用驼峰格式的字段
        const formattedList = list.map(item => ({
          ...item,
          methodCode: item.methodCode || item.method_code,
          methodName: item.methodName || item.method_name,
          requiredFields: item.requiredFields || item.required_fields || [],
          optionalFields: item.optionalFields || item.optional_fields || [],
          feeRate: item.feeRate || parseFloat(item.fee_rate || 0),
          feeFixed: item.feeFixed || parseFloat(item.fee_fixed || 0),
          minAmount: item.minAmount || parseFloat(item.min_amount || 0),
          maxAmount: item.maxAmount !== undefined ? item.maxAmount : (item.max_amount ? parseFloat(item.max_amount) : null),
          dailyLimit: item.dailyLimit || parseFloat(item.daily_limit || 0),
          sortOrder: item.sortOrder || item.sort_order || 0,
        }));
        setMethods(formattedList);
      }
    } catch (error) {
      message.error('获取提现方式列表失败');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // 打开添加/编辑弹窗
  const handleOpenModal = (record = null) => {
    setEditingMethod(record);
    if (record) {
      // 确保使用正确的字段格式
      form.setFieldsValue({
        methodCode: record.methodCode || record.method_code,
        methodName: record.methodName || record.method_name,
        requiredFields: record.requiredFields || record.required_fields || [],
        optionalFields: record.optionalFields || record.optional_fields || [],
        feeRate: record.feeRate || parseFloat(record.fee_rate || 0.001),
        feeFixed: record.feeFixed || parseFloat(record.fee_fixed || 0),
        minAmount: record.minAmount || parseFloat(record.min_amount || 100),
        maxAmount: record.maxAmount !== undefined ? record.maxAmount : (record.max_amount ? parseFloat(record.max_amount) : null),
        dailyLimit: record.dailyLimit || parseFloat(record.daily_limit || 0),
        sortOrder: record.sortOrder || record.sort_order || 0,
        status: record.status === 1 || record.status === true,
        supportMerchant: record.supportMerchant === 1 || record.supportMerchant === true,
        remark: record.remark || '',
      });
    } else {
      form.resetFields();
      // 初始化必填和可选字段为空数组
      form.setFieldsValue({
        requiredFields: [],
        optionalFields: [],
        feeRate: 0.001,
        feeFixed: 0,
        minAmount: 100,
        status: true,
        supportMerchant: true,
        sortOrder: 0,
      });
    }
    setModalVisible(true);
  };

  // 关闭弹窗
  const handleCloseModal = () => {
    setModalVisible(false);
    setEditingMethod(null);
    form.resetFields();
  };

  // 提交表单
  const handleSubmit = async (values) => {
    try {
      // 验证字段配置
      const requiredFields = values.requiredFields || [];
      const optionalFields = values.optionalFields || [];

      // 检查必填字段配置
      for (const field of requiredFields) {
        if (!field.identifier || !field.label || !field.type) {
          message.error('请完整填写必填字段的配置（标识、标签、类型）');
          return;
        }
        if (field.required !== true) {
          message.error('必填字段的"是否必填"必须为"是"');
          return;
        }
      }

      // 检查可选字段配置
      for (const field of optionalFields) {
        if (!field.identifier || !field.label || !field.type) {
          message.error('请完整填写可选字段的配置（标识、标签、类型）');
          return;
        }
      }

      const payload = {
        methodCode: values.methodCode,
        methodName: values.methodName,
        requiredFields: requiredFields.filter(f => f.identifier && f.label && f.type),
        optionalFields: optionalFields.filter(f => f.identifier && f.label && f.type),
        feeRate: values.feeRate || 0.001,
        feeFixed: values.feeFixed || 0,
        minAmount: values.minAmount || 0,
        maxAmount: values.maxAmount || null,
        dailyLimit: values.dailyLimit || 0,
        status: values.status ? 1 : 0,
        supportMerchant: values.supportMerchant ? 1 : 0,
        sortOrder: values.sortOrder || 0,
        remark: values.remark || '',
      };

      let res;
      if (editingMethod) {
        res = await put(`/admin/withdraw-methods/${editingMethod.id}`, payload);
      } else {
        res = await post('/admin/withdraw-methods', payload);
      }

      if (res.code === 200) {
        message.success(editingMethod ? '提现方式更新成功' : '提现方式创建成功');
        handleCloseModal();
        fetchMethods();
      }
    } catch (error) {
      message.error(error.message || '操作失败');
      console.error(error);
    }
  };

  // 删除提现方式
  const handleDelete = async (id) => {
    try {
      const res = await del(`/admin/withdraw-methods/${id}`);
      if (res.code === 200) {
        message.success('提现方式删除成功');
        fetchMethods();
      }
    } catch (error) {
      message.error(error.message || '删除失败');
      console.error(error);
    }
  };

  // 切换状态
  const handleToggleStatus = async (record) => {
    try {
      const newStatus = record.status === 1 ? 0 : 1;
      const res = await put(`/admin/withdraw-methods/${record.id}/status`, { status: newStatus });
      if (res.code === 200) {
        message.success('状态更新成功');
        fetchMethods();
      }
    } catch (error) {
      message.error(error.message || '操作失败');
      console.error(error);
    }
  };

  // 字段类型选项
  const fieldTypes = [
    { label: '文本', value: 'text' },
    { label: '数字', value: 'number' },
    { label: '下拉选择', value: 'select' },
    { label: '多行文本', value: 'textarea' },
  ];

  // 表格列定义
  const columns = [
    {
      title: '方式名称',
      key: 'methodName',
      width: 150,
      render: (_, record) => record.methodName || record.method_name || '-',
    },
    {
      title: '方式编码',
      key: 'methodCode',
      width: 120,
      render: (_, record) => {
        const code = record.methodCode || record.method_code;
        return <Tag color="blue">{code}</Tag>;
      },
    },
    {
      title: '字段配置',
      key: 'fields',
      render: (_, record) => {
        const requiredFields = record.requiredFields || record.required_fields || [];
        const optionalFields = record.optionalFields || record.optional_fields || [];
        const requiredCount = Array.isArray(requiredFields) ? requiredFields.length : 0;
        const optionalCount = Array.isArray(optionalFields) ? optionalFields.length : 0;
        return (
          <Space>
            <Tag color="red">必填: {requiredCount}</Tag>
            <Tag color="blue">可选: {optionalCount}</Tag>
          </Space>
        );
      },
    },
    // {
    //   title: '手续费',
    //   key: 'fee',
    //   width: 150,
    //   render: (_, record) => {
    //     const feeRate = record.feeRate || parseFloat(record.fee_rate || 0);
    //     const feeFixed = record.feeFixed || parseFloat(record.fee_fixed || 0);
    //     const rate = (feeRate * 100).toFixed(2);
    //     const fixed = parseFloat(feeFixed);
    //     if (fixed > 0) {
    //       return `${rate}% + ¥${fixed}`;
    //     }
    //     return `${rate}%`;
    //   },
    // },
    // {
    //   title: '限额',
    //   key: 'limits',
    //   width: 200,
    //   render: (_, record) => {
    //     const minAmount = record.minAmount || parseFloat(record.min_amount || 0);
    //     const maxAmount = record.maxAmount !== undefined ? record.maxAmount : (record.max_amount ? parseFloat(record.max_amount) : null);
    //     const min = parseFloat(minAmount);
    //     const max = maxAmount ? parseFloat(maxAmount) : '不限';
    //     return `¥${min} - ${max === '不限' ? max : '¥' + max}`;
    //   },
    // },
    {
      title: '状态',
      key: 'status',
      width: 100,
      render: (_, record) => {
        const status = record.status;
        return (
          <Switch
            checked={status === 1 || status === true}
            onChange={() => handleToggleStatus(record)}
          />
        );
      },
    },
    {
      title: '操作',
      key: 'action',
      width: 150,
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleOpenModal(record)}
          >
            编辑
          </Button>
          <Popconfirm
            title="确定删除此提现方式吗？"
            description="删除后无法恢复，且已有提现记录使用此方式时无法删除"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
            >
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">提现方式管理</h1>
        <p className="page-description">配置和管理平台的提现方式，支持动态字段配置</p>
      </div>

      <Card>
        <div style={{ marginBottom: 16 }}>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => handleOpenModal()}
          >
            添加提现方式
          </Button>
        </div>

        <Table
          columns={columns}
          dataSource={methods}
          rowKey="id"
          loading={loading}
          pagination={false}
        />
      </Card>

      {/* 添加/编辑弹窗 */}
      <Modal
        title={editingMethod ? '编辑提现方式' : '添加提现方式'}
        open={modalVisible}
        onCancel={handleCloseModal}
        footer={null}
        width={900}
        style={{ top: 20 }}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            requiredFields: [],
            optionalFields: [],
            feeRate: 0.001,
            feeFixed: 0,
            minAmount: 100,
            status: true,
            supportMerchant: true,
            sortOrder: 0,
          }}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="methodCode"
                label="方式编码"
                rules={[
                  { required: true, message: '请输入方式编码' },
                  { pattern: /^[a-z_]+$/, message: '只能包含小写字母和下划线' },
                ]}
                tooltip="用于标识提现方式，如：bank_card、usdt"
              >
                <Input 
                  placeholder="如：bank_card" 
                  disabled={!!editingMethod}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="methodName"
                label="方式名称"
                rules={[{ required: true, message: '请输入方式名称' }]}
              >
                <Input placeholder="如：银行卡" />
              </Form.Item>
            </Col>
          </Row>

          {/* <Divider orientation="left">手续费配置</Divider>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="feeRate"
                label="手续费率"
                rules={[{ required: true, message: '请输入手续费率' }]}
                tooltip="例如：0.001 表示 0.1%"
              >
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  max={1}
                  step={0.0001}
                  precision={6}
                  placeholder="0.001"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="feeFixed"
                label="固定手续费（元）"
              >
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  precision={2}
                  placeholder="0"
                />
              </Form.Item>
            </Col>
          </Row> */}

          {/* <Divider orientation="left">限额配置</Divider>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="minAmount"
                label="最小金额（元）"
                rules={[{ required: true, message: '请输入最小金额' }]}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  precision={2}
                  placeholder="100"
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="maxAmount"
                label="最大金额（元）"
                tooltip="留空表示不限制"
              >
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  precision={2}
                  placeholder="留空不限制"
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="dailyLimit"
                label="每日限额（元）"
                tooltip="0表示不限制"
              >
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  precision={2}
                  placeholder="0"
                />
              </Form.Item>
            </Col>
          </Row> */}

          <Divider orientation="left">字段配置</Divider>
          
          {/* 必填字段配置 */}
          <Form.Item label="必填字段">
            <Form.List name="requiredFields">
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, ...restField }) => (
                    <Card
                      key={key}
                      size="small"
                      style={{ marginBottom: 16, background: '#fff5f5' }}
                      extra={
                        <Button
                          type="link"
                          danger
                          icon={<MinusCircleOutlined />}
                          onClick={() => remove(name)}
                        >
                          删除
                        </Button>
                      }
                    >
                      <Row gutter={16}>
                        <Col span={6}>
                          <Form.Item
                            {...restField}
                            name={[name, 'identifier']}
                            rules={[{ required: true, message: '必填' }]}
                            label="标识"
                            tooltip="字段唯一标识，如：realName"
                          >
                            <Input placeholder="如：realName" />
                          </Form.Item>
                        </Col>
                        <Col span={6}>
                          <Form.Item
                            {...restField}
                            name={[name, 'label']}
                            rules={[{ required: true, message: '必填' }]}
                            label="标签"
                          >
                            <Input placeholder="如：真实姓名" />
                          </Form.Item>
                        </Col>
                        <Col span={6}>
                          <Form.Item
                            {...restField}
                            name={[name, 'type']}
                            rules={[{ required: true, message: '必填' }]}
                            label="类型"
                          >
                            <Select placeholder="选择类型">
                              {fieldTypes.map(type => (
                                <Option key={type.value} value={type.value}>
                                  {type.label}
                                </Option>
                              ))}
                            </Select>
                          </Form.Item>
                        </Col>
                        <Col span={6}>
                          <Form.Item
                            {...restField}
                            name={[name, 'required']}
                            label="是否必填"
                            valuePropName="checked"
                            initialValue={true}
                          >
                            <Switch disabled checked={true} />
                          </Form.Item>
                        </Col>
                      </Row>
                    </Card>
                  ))}
                  <Button
                    type="dashed"
                    onClick={() => add()}
                    block
                    icon={<PlusOutlined />}
                  >
                    添加必填字段
                  </Button>
                </>
              )}
            </Form.List>
          </Form.Item>

          {/* 可选字段配置 */}
          <Form.Item label="可选字段">
            <Form.List name="optionalFields">
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, ...restField }) => (
                    <Card
                      key={key}
                      size="small"
                      style={{ marginBottom: 16, background: '#f0f9ff' }}
                      extra={
                        <Button
                          type="link"
                          danger
                          icon={<MinusCircleOutlined />}
                          onClick={() => remove(name)}
                        >
                          删除
                        </Button>
                      }
                    >
                      <Row gutter={16}>
                        <Col span={6}>
                          <Form.Item
                            {...restField}
                            name={[name, 'identifier']}
                            rules={[{ required: true, message: '必填' }]}
                            label="标识"
                          >
                            <Input placeholder="如：branchName" />
                          </Form.Item>
                        </Col>
                        <Col span={6}>
                          <Form.Item
                            {...restField}
                            name={[name, 'label']}
                            rules={[{ required: true, message: '必填' }]}
                            label="标签"
                          >
                            <Input placeholder="如：开户支行" />
                          </Form.Item>
                        </Col>
                        <Col span={6}>
                          <Form.Item
                            {...restField}
                            name={[name, 'type']}
                            rules={[{ required: true, message: '必填' }]}
                            label="类型"
                          >
                            <Select placeholder="选择类型">
                              {fieldTypes.map(type => (
                                <Option key={type.value} value={type.value}>
                                  {type.label}
                                </Option>
                              ))}
                            </Select>
                          </Form.Item>
                        </Col>
                        <Col span={6}>
                          <Form.Item
                            {...restField}
                            name={[name, 'required']}
                            label="是否必填"
                            valuePropName="checked"
                            initialValue={false}
                          >
                            <Switch disabled checked={false} />
                          </Form.Item>
                        </Col>
                      </Row>
                    </Card>
                  ))}
                  <Button
                    type="dashed"
                    onClick={() => add()}
                    block
                    icon={<PlusOutlined />}
                  >
                    添加可选字段
                  </Button>
                </>
              )}
            </Form.List>
          </Form.Item>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="supportMerchant"
                label="商户代付"
                valuePropName="checked"
                tooltip="开启后商户可以使用该方式进行代付"
              >
                <Switch />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="sortOrder"
                label="排序"
              >
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  placeholder="排序权重，数字越小越靠前"
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="status"
                label="状态"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="remark"
            label="备注"
          >
            <TextArea rows={3} placeholder="备注信息" />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                {editingMethod ? '更新' : '创建'}
              </Button>
              <Button onClick={handleCloseModal}>取消</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default WithdrawMethods;

